import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { registerSW } from 'virtual:pwa-register'
import '@fontsource-variable/montserrat/wght.css'
import '@fontsource/poppins/400.css'
import '@fontsource/poppins/500.css'
import '@fontsource/poppins/600.css'
import './index.css'
import { Providers } from './app/Providers'
import { App } from './app/App'
import { ErrorBoundary } from './shared/components/ErrorBoundary'
import {
  installChunkReloadHandler,
  isDynamicImportError,
} from './shared/lib/chunkReload'
import { cleanupLegacyKeys } from './shared/lib/storage'
import { purgeOrphanedCaches } from './shared/lib/swHealing'

installChunkReloadHandler()
cleanupLegacyKeys()
purgeOrphanedCaches()

// PWA service worker auto-update.
//   - immediate: true → register the SW as soon as the script runs and reload
//     the page when a new SW activates. Combined with our shell-only no-cache
//     policy and short forms, the data-loss risk is minimal and the upside —
//     never having to ask the user to close/reopen the app to pick up a fix
//     — is large.
//   - 5-minute polling → without it the browser only re-checks the SW URL on
//     navigation (or every 24 h in standalone), so a PWA left open by a user
//     would never see a new deploy until they manually refreshed. Polling
//     calls registration.update(), which cache-busts the SW request itself.
const SW_UPDATE_INTERVAL_MS = 5 * 60 * 1000
registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return
    const handle = setInterval(() => {
      if (registration.installing || !navigator.onLine) return
      void registration.update().catch(() => {
        /* swallow transient network errors */
      })
    }, SW_UPDATE_INTERVAL_MS)
    // Avoid stacking intervals across Vite HMR re-runs of this module.
    if (import.meta.hot) {
      import.meta.hot.dispose(() => clearInterval(handle))
    }
  },
})

Sentry.init({
  dsn: 'https://eac60e225e49d5e3e48d507bb831f4aa@o4508966792658944.ingest.de.sentry.io/4511217104257104',
  sendDefaultPii: true,
  // Drop non-actionable service-worker registration rejections (private mode,
  // enterprise SW policies, iOS memory pressure, transient MIME issues).
  ignoreErrors: [/^Rejected$/],
  beforeSend(event, hint) {
    const err = hint.originalException as Error | undefined
    const exception = event.exception?.values?.[0]
    // Dynamic import failures are auto-recovered via reload (see chunkReload.ts);
    // the original error fires before the reload completes, so suppress it.
    // When the exception is captured by Sentry without a raw Error (e.g. from
    // `unhandledrejection`), `exception.type` holds the error name.
    const errForCheck = err ?? {
      name: exception?.type,
      message: exception?.value,
    }
    if (isDynamicImportError(errForCheck)) return null
    const frames = exception?.stacktrace?.frames ?? []
    const fromRegisterSW = frames.some((f) =>
      f.filename?.includes('registerSW.js'),
    )
    // Match both Error instances and non-Error rejections (Sentry puts the
    // latter in exception.value).
    const message = err?.message ?? exception?.value
    if (fromRegisterSW && message === 'Rejected') return null
    return event
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <Providers>
        <App />
      </Providers>
    </ErrorBoundary>
  </StrictMode>,
)
